import { SelectOption } from 'components/tables/list-table/ListTableTypes';
import { useEffect, useRef, useState } from 'react';

const DEFAULT_MAX_ROWS = 3;

interface OptionsInactiveMaxRowsProps {
    maxRows?: number;
    selectedOptions: SelectOption[];
    // When false, the field stays collapsed (measured row count + "+N") even while focused, instead
    // of expanding to show every selected chip. Used by the compact toolbar multiselect.
    expandOnFocus?: boolean;
    // Floor on how many chips stay visible once collapsed. Keeps at least one name in front of the
    // "+N" instead of collapsing to a bare "+N" when a long first chip eats the whole row.
    minVisibleTags?: number;
}

export const useOptionsInactiveMaxRows = ({
    selectedOptions,
    maxRows = DEFAULT_MAX_ROWS,
    expandOnFocus = true,
    minVisibleTags = 0,
}: OptionsInactiveMaxRowsProps) => {
    const measureRef = useRef<HTMLDivElement | null>(null);
    const [focused, setFocused] = useState<boolean>(false);
    const [visibleCount, setVisibleCount] = useState(selectedOptions.length);
    const hiddenCount = selectedOptions.length - visibleCount;

    // TODO(react-doctor: no-cascading-set-state): single setVisibleCount per run
    // but multiple branches; layout-measurement effect requires post-render DOM
    // access (offsetTop), so it cannot move into a handler.
    useEffect(() => {
        if (!measureRef.current || (focused && expandOnFocus)) {
            // eslint-disable-next-line react-hooks-js/set-state-in-effect -- layout measurement: visibleCount derived from post-render DOM offsetTop of chip elements
            setVisibleCount(selectedOptions.length);
            return;
        }

        const chips = Array.from(measureRef.current.children) as HTMLElement[];
        if (chips.length === 0) return;

        // Track each chip's row by its offsetTop
        const rowTops: number[] = [];
        const chipRows: number[] = [];

        chips.forEach((chip) => {
            const top = chip.offsetTop;
            let rowIndex = rowTops.indexOf(top);
            if (rowIndex === -1) {
                rowTops.push(top);
                rowIndex = rowTops.length - 1;
            }
            chipRows.push(rowIndex);
        });

        const totalRows = rowTops.length;

        if (totalRows <= maxRows) {
            setVisibleCount(Math.max(0, selectedOptions.length));
        } else {
            // Over max rows — find how many real chips fit in 3 rows (excluding +N)
            const maxRowIndex = maxRows - 1;
            let lastFittingChipIndex = -1;
            for (let i = chipRows.length - 1; i >= 0; i--) {
                if (chipRows[i] <= maxRowIndex) {
                    lastFittingChipIndex = i;
                    break;
                }
            }
            // Back off one more to leave space for +N, but never below the requested floor so a long
            // first chip can't collapse the field to a bare "+N".
            setVisibleCount(Math.min(selectedOptions.length, Math.max(minVisibleTags, lastFittingChipIndex)));
        }
    }, [selectedOptions, focused, maxRows, expandOnFocus, minVisibleTags]);

    return {
        hiddenCount,
        visibleCount,
        focused,
        setFocused,
        measureRef,
    };
};
